import { Link } from "react-router-dom";
import { BookOpen, Check, Download, Loader2 } from "lucide-react";

export const EvidenceList = ({ result, user, oaImports, bulkImporting, onImportOne, onImportAll }) => (
  <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <div className="flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-orange-600" />
        <h2 className="text-2xl font-semibold text-slate-950">Evidence used</h2>
      </div>
      {user?.role === "admin" && result.sources.some((s) => s.source_kind === "open_access") && (
        <button
          type="button"
          onClick={onImportAll}
          disabled={bulkImporting}
          className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-4 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60"
          data-testid="research-import-oa-all"
        >
          {bulkImporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Import all open-access sources
        </button>
      )}
    </div>
    <div className="space-y-4">
      {result.sources.map((source) => (
        <article key={source.ref_id} id={`source-${source.ref_id}`} className="rounded-lg border border-slate-200 p-4 scroll-mt-24">
          <div className="flex gap-3">
            <span className={`shrink-0 rounded text-white text-xs font-semibold px-2 py-1 h-fit ${source.source_kind === "open_access" ? "bg-sky-700" : "bg-slate-900"}`}>{source.ref_id}</span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {source.source_kind === "open_access" ? (
                  <a href={source.external_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-950 hover:text-sky-700 hover:underline">{source.title}</a>
                ) : (
                  <Link to={`/items/${source.item_id}`} className="font-semibold text-slate-950 hover:text-orange-700 hover:underline">{source.title}</Link>
                )}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${source.source_kind === "open_access" ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-600"}`}>{source.source_kind === "open_access" ? "Open access · OpenAlex" : "Your library"}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{source.authors?.join(", ") || "Unknown author"}{source.year ? ` · ${source.year}` : ""}</p>
              {source.source_kind === "open_access" && (source.oa_status || source.license) && <p className="text-[11px] text-sky-700 mt-1">{source.oa_status ? `${source.oa_status} open access` : "Open access"}{source.license ? ` · ${source.license}` : ""}</p>}
              {user?.role === "admin" && source.source_kind === "open_access" && (
                (oaImports[source.ref_id]?.status === "done" || source.imported_item_id) ? (
                  <Link
                    to={`/items/${oaImports[source.ref_id]?.itemId || source.imported_item_id}`}
                    className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-emerald-700 hover:underline"
                    data-testid={`research-import-oa-done-${source.ref_id}`}
                  >
                    <Check className="w-3.5 h-3.5" /> In library — open item
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => onImportOne(source.ref_id)}
                    className="inline-flex items-center gap-1 mt-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                    data-testid={`research-import-oa-${source.ref_id}`}
                  >
                    {oaImports[source.ref_id]?.status === "importing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Add to library
                  </button>
                )
              )}
              {source.evidence?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs font-semibold text-orange-700 cursor-pointer">{source.source_kind === "open_access" ? "View supporting abstract" : "View supporting excerpts"}</summary>
                  <div className="mt-3 space-y-2">
                    {source.evidence.map((excerpt, index) => <blockquote key={index} className="border-l-2 border-orange-200 pl-3 text-xs leading-5 text-slate-600">{excerpt}</blockquote>)}
                  </div>
                </details>
              )}
            </div>
          </div>
        </article>
      ))}
    </div>
  </section>
);
